import { ReactElement, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';

import { EvaluatedSettingMetadata, RawSetting } from '../../../types/UserSettings';

export interface SettingsRowData {
    key: string;
    setting: RawSetting;
    metadata?: EvaluatedSettingMetadata;
}

interface Props {
    rows: SettingsRowData[];
    onEdit?: (key: string) => void;
}

interface SourceOrOverridesCellProps {
    metadata?: EvaluatedSettingMetadata;
    setting: RawSetting;
    canEdit: boolean;
}

function SourceOrOverridesCell({
    metadata,
    setting,
    canEdit
}: Readonly<SourceOrOverridesCellProps>): ReactElement | null {
    if (metadata) {
        return (
            <TableCell>
                <Chip
                    label={metadata.source}
                    size="small"
                    color={metadata.source === 'override' ? 'primary' : 'default'}
                />
                {metadata.source === 'override' && metadata.matchedSelector && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {Object.entries(metadata.matchedSelector)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                            .join(' · ')}
                        {metadata.overrideDescription && ` — ${metadata.overrideDescription}`}
                    </Typography>
                )}
            </TableCell>
        );
    }
    if (canEdit) {
        return (
            <TableCell>
                {setting.overrides && setting.overrides.length > 0 ? (
                    <Chip
                        label={`${setting.overrides.length} override${setting.overrides.length > 1 ? 's' : ''}`}
                        size="small"
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        —
                    </Typography>
                )}
            </TableCell>
        );
    }
    return null;
}

function SettingsTable({ rows, onEdit }: Readonly<Props>): ReactElement {
    const [search, setSearch] = useState('');

    const filtered = rows.filter(
        ({ key, setting }) =>
            key.toLowerCase().includes(search.toLowerCase()) ||
            setting.description.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box>
            <TextField
                placeholder="Search settings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ mb: 2, width: 320 }}
                inputProps={{ 'data-testid': 'settings-search' }}
            />
            <TableContainer component={Paper}>
                <Table size="small" data-testid="settings-table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Setting</TableCell>
                            <TableCell>Value</TableCell>
                            {rows.some((r) => r.metadata) && <TableCell>Source</TableCell>}
                            {rows.some((r) => !r.metadata && onEdit) && <TableCell>Overrides</TableCell>}
                            {onEdit && <TableCell align="right">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map(({ key, setting, metadata }) => (
                            <TableRow key={key} data-testid={`settings-row-${key}`}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {key}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {setting.description}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
                                        {JSON.stringify(setting.value, null, 2)}
                                    </Typography>
                                </TableCell>
                                <SourceOrOverridesCell metadata={metadata} setting={setting} canEdit={!!onEdit} />
                                {onEdit && (
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(key)}
                                            data-testid={`edit-setting-${key}`}
                                            aria-label={`Edit ${key}`}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No settings match your search.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default SettingsTable;
